import { getDocument } from '@atlaskit/browser-apis';

/** Capped exponential backoff with the same jitter shape as graphql-ws. */
export const createRetryWait =
	(maxRetryWait: number) =>
	async (retries: number): Promise<void> => {
		const jitter = Math.floor(Math.random() * 2700 + 300);
		const delay = Math.min(1000 * 2 ** retries + jitter, maxRetryWait);
		await new Promise((resolve) => {
			setTimeout(resolve, delay);
		});
	};

/** Retry non-Error failures. graphql-ws treats bare socket errors as permanent. */
export const shouldRetryTransportFailure = (errOrCloseEvent: unknown): boolean =>
	!(errOrCloseEvent instanceof Error);

/**
 * Calls terminate() on online/visible when disconnected; always on bfcache restore and resume.
 */
export const addRecoveryListeners = ({
	terminate,
	isConnected,
}: {
	terminate: () => void;
	isConnected: () => boolean;
}): (() => void) => {
	const doc = getDocument();

	const wakeIfDisconnected = (): void => {
		if (!isConnected()) {
			terminate();
		}
	};

	const onVisibilityChange = (): void => {
		if (doc?.visibilityState === 'visible') {
			wakeIfDisconnected();
		}
	};

	const onPageShow = (event: PageTransitionEvent): void => {
		// pageshow also fires on ordinary loads; only `persisted` is bfcache.
		if (event.persisted) {
			terminate();
		}
	};

	const onResume = (): void => {
		terminate();
	};

	window.addEventListener('online', wakeIfDisconnected);
	window.addEventListener('pageshow', onPageShow);
	doc?.addEventListener('visibilitychange', onVisibilityChange);
	// Chrome tab freeze; no pageshow.
	doc?.addEventListener('resume', onResume);

	return () => {
		window.removeEventListener('online', wakeIfDisconnected);
		window.removeEventListener('pageshow', onPageShow);
		doc?.removeEventListener('visibilitychange', onVisibilityChange);
		doc?.removeEventListener('resume', onResume);
	};
};
